import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuList,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import { useGetAuditLogFiltersQuery, useListAuditLogQuery } from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { ListDownloadItems } from "../../../../../common/download/ListDownloadItems";
import { osloDate } from "../../../../../common/download/listExport";
import { displayDate } from "../../../../../common/formatting/displayDate";
import { useDisplayOsloDateWithTime } from "../../../../../common/formatting/useDisplayOsloDateWithTime";
import { Paging } from "../../../../../common/Paging";
import { SettingsCard } from "../SettingsCard";
import { auditEventLabel } from "./auditLogLabels";
import {
  auditLogColumns,
  auditLogDownloadErrorMessage,
  auditLogPersonLabels,
  auditLogRow,
  type AuditLogRow,
} from "./auditLogRows";

export const PAGE_SIZE = 50;
const DEFAULT_PERIOD_DAYS = 30;

interface Filters {
  from: string;
  to: string;
  person: string;
  eventType: string;
}

export const defaultFilters = (now: Date = new Date()): Filters => ({
  from: osloDate(new Date(now.getTime() - (DEFAULT_PERIOD_DAYS - 1) * 86_400_000).toISOString()),
  to: osloDate(now.toISOString()),
  person: "",
  eventType: "",
});

export const AuditLog = () => {
  const { t } = useTranslation();
  const orgId = useLoggedInWithOrgContextState().selectedOrg.id;
  const [filters, setFilters] = useState<Filters>(() => defaultFilters());
  const [positions, setPositions] = useState<(string | undefined)[]>([undefined]);
  const [page, setPage] = useState(0);
  const periodIsBackwards = filters.from !== "" && filters.to !== "" && filters.to < filters.from;

  const query = queryFor(orgId, filters);
  const { currentData, isFetching, isError, refetch } = useListAuditLogQuery(
    { ...query, after: positions[page], limit: PAGE_SIZE },
    { skip: periodIsBackwards },
  );
  const {
    data: options,
    isLoading: optionsLoading,
    isError: optionsFailed,
    refetch: refetchOptions,
  } = useGetAuditLogFiltersQuery({ orgId });
  const personLabels = useMemo(() => auditLogPersonLabels(options), [options]);
  const rows = useMemo(
    () => (currentData?.entries ?? []).map((entry) => auditLogRow(t, entry, personLabels)),
    [currentData, t, personLabels],
  );

  const change = (patch: Partial<Filters>) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPositions([undefined]);
    setPage(0);
  };

  const goTo = (skip: number) => {
    const next = skip / PAGE_SIZE;
    if (next > page && currentData?.nextPosition) {
      setPositions((known) => [...known.slice(0, next), currentData.nextPosition ?? undefined]);
    }
    setPage(next);
  };

  return (
    <>
      <Text fontSize="sm" color="smMuted" mb="4">
        {t("Everything people, support and integrations did in the organization, newest first.")}
      </Text>
      <Flex gap="4" wrap="wrap" align="flex-end" mb="4">
        <FormControl width="auto">
          <FormLabel fontSize="sm">{t("From")}</FormLabel>
          <Input
            type="date"
            size="sm"
            value={filters.from}
            max={filters.to || undefined}
            onChange={(e) => change({ from: e.target.value })}
          />
        </FormControl>
        <FormControl width="auto">
          <FormLabel fontSize="sm">{t("To")}</FormLabel>
          <Input
            type="date"
            size="sm"
            value={filters.to}
            min={filters.from || undefined}
            onChange={(e) => change({ to: e.target.value })}
          />
        </FormControl>
        <FormControl width="auto" minWidth="48">
          <FormLabel fontSize="sm">{t("Done by")}</FormLabel>
          <Select
            size="sm"
            value={filters.person}
            isDisabled={optionsLoading}
            onChange={(e) => change({ person: e.target.value })}
          >
            <option value="">{optionsLoading ? t("Loading…") : t("Everyone")}</option>
            {(options?.people ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {personLabels.get(person.id)}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl width="auto" minWidth="48">
          <FormLabel fontSize="sm">{t("Event_audit_log")}</FormLabel>
          <Select
            size="sm"
            value={filters.eventType}
            isDisabled={optionsLoading}
            onChange={(e) => change({ eventType: e.target.value })}
          >
            <option value="">{optionsLoading ? t("Loading…") : t("All events")}</option>
            {options?.eventTypes.map((eventType) => (
              <option key={eventType} value={eventType}>
                {auditEventLabel(t, eventType)}
              </option>
            ))}
          </Select>
        </FormControl>
        <Box ml={{ base: 0, md: "auto" }}>
          <AuditLogDownloadMenu
            orgId={orgId}
            filters={filters}
            isDisabled={periodIsBackwards || isError || rows.length === 0}
          />
        </Box>
      </Flex>
      {optionsFailed && (
        <Flex align="center" gap="3" mb="4" wrap="wrap">
          <Text fontSize="sm" color="smError">
            {t("Could not load the people and events to filter by.")}
          </Text>
          <Button size="xs" variant="outline" onClick={() => void refetchOptions()}>
            {t("Try again")}
          </Button>
        </Flex>
      )}
      <Paging
        limit={PAGE_SIZE}
        skip={page * PAGE_SIZE}
        setSkip={goTo}
        isLoading={isFetching}
        hasNextPage={Boolean(currentData?.nextPosition)}
      />
      <SettingsCard title={t("Audit log")} subtitle={periodLabel(t, filters)} inset={false}>
        <AuditLogBody
          rows={rows}
          isLoading={isFetching && !currentData}
          isError={isError}
          periodIsBackwards={periodIsBackwards}
          onRetry={() => void refetch()}
        />
      </SettingsCard>
    </>
  );
};

const AuditLogBody = ({
  rows,
  isLoading,
  isError,
  periodIsBackwards,
  onRetry,
}: {
  rows: AuditLogRow[];
  isLoading: boolean;
  isError: boolean;
  periodIsBackwards: boolean;
  onRetry: () => void;
}) => {
  const { t } = useTranslation();
  const displayTime = useDisplayOsloDateWithTime();
  if (periodIsBackwards) {
    return <Message>{t("The period ends before it starts. Choose a later end date.")}</Message>;
  }
  if (isLoading) {
    return (
      <Flex justify="center" py="10">
        <Spinner color="smPrimary" />
      </Flex>
    );
  }
  if (isError) {
    return (
      <Flex direction="column" align="center" py="10" gap="3">
        <Text color="smError">{t("Could not load the audit log.")}</Text>
        <Button size="sm" variant="outline" onClick={onRetry}>
          {t("Try again")}
        </Button>
      </Flex>
    );
  }
  if (rows.length === 0) {
    return <Message>{t("No entries match. Choose an earlier start date or another person or event.")}</Message>;
  }
  return (
    <Box overflowX="auto">
      <Table size="sm" variant="simple" width="full" minWidth="900px">
        <Thead>
          <Tr>
            {auditLogColumns(t).map((column, index) => (
              <Th key={column.key} pl={index === 0 ? "5" : undefined} borderBottomColor="smBorderSubtle">
                {column.label}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr key={row.id}>
              <Td pl="5" whiteSpace="nowrap">
                {displayTime(row.occurredAt)}
              </Td>
              <Td>
                <Text>{row.person}</Text>
                {row.bySupport && <Badge colorScheme="purple">{t("SourceMagnet Support")}</Badge>}
              </Td>
              <Td>
                <Text>{row.event}</Text>
                {row.fromHistory && <Badge>{t("From earlier history")}</Badge>}
              </Td>
              <Td>
                {row.itemLink ? (
                  <Link as={RouterLink} to={row.itemLink} color="smPrimary">
                    {row.item}
                  </Link>
                ) : (
                  row.item
                )}
              </Td>
              <Td>
                {row.details.map((line) => (
                  <Text key={line} fontSize="sm" color="smMuted">
                    {line}
                  </Text>
                ))}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

const Message = ({ children }: { children: string }) => (
  <Box px="5" py="6">
    <Text fontSize="sm" color="smMuted">
      {children}
    </Text>
  </Box>
);

const AuditLogDownloadMenu = ({
  orgId,
  filters,
  isDisabled,
}: {
  orgId: string;
  filters: Filters;
  isDisabled: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Menu isLazy closeOnSelect={false} placement="bottom-end">
      {({ onClose }) => (
        <>
          <MenuButton as={Button} size="sm" variant="outline" rightIcon={<FiChevronDown />} isDisabled={isDisabled}>
            {t("Download")}
          </MenuButton>
          <MenuList>
            <ListDownloadItems
              label={t("Audit log")}
              endpoint={`/api/orgs/${orgId}/audit-log/list-export`}
              baseFileName="Revisjonslogg"
              errorMessage={(error, format) => auditLogDownloadErrorMessage(t, error, format)}
              filters={filterQuery(filters)}
              isLoading={false}
              isDisabled={isDisabled}
              hint={String(t("Every entry that matches the filters, with the columns shown on screen."))}
              onDone={onClose}
            />
          </MenuList>
        </>
      )}
    </Menu>
  );
};

const queryFor = (orgId: string, filters: Filters) => ({ orgId, ...filterQuery(filters) });

const filterQuery = (filters: Filters): Record<string, string> => ({
  ...(filters.from ? { from: filters.from } : {}),
  ...(filters.to ? { to: filters.to } : {}),
  ...(filters.person ? { person: filters.person } : {}),
  ...(filters.eventType ? { eventType: filters.eventType } : {}),
});

const periodLabel = (t: TFunction, filters: Filters): string =>
  String(
    t("{{from}} to {{to}}", {
      from: filters.from ? displayDate(filters.from) : "…",
      to: filters.to ? displayDate(filters.to) : "…",
    }),
  );
